import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useLocation, createSearchParams } from 'react-router-dom'
import { RootStore } from '../../../redux/store'

const validRoutes = [
  '/dashboard',
  '/myavailability',
  '/myextras',
  '/wallchart',
  '/booking',
  '/myactions',
  '/absenceandlate',
  '/holiday',
  '/enhancements',
  '/settings',
]

const useBackButton = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const isValidLocation = useCallback(
    (route: string): boolean =>
      validRoutes.some(validRoute => route === validRoute || route.startsWith(`${validRoute}/`)),
    []
  )
  const activeRoute = useSelector<RootStore, string>(
    (state: RootStore) => state.appSettings.activeRoute
  )

  const userPermissions = useSelector<RootStore, string[]>(
    (state: RootStore) => state.userState.permissions
  )
  const isManager = useMemo(() => userPermissions.includes('ViewMyActions'), [userPermissions])

  const isExternalNavigation = useMemo(() => window.history.length === 1, [])

  const handleNavigation = useCallback(() => {
    if (isExternalNavigation) {
      navigate(isManager ? '/myactions' : '/myavailability')
      return
    }

    if (
      location.state &&
      location.state.previousLocation &&
      isValidLocation(location.state.previousLocation as string)
    ) {
      const navigateTo = location.state.previousLocation as string
      const stateToPass = location.state.originalPreviousLocation ? {
        ...location.state,
        previousLocation: location.state.originalPreviousLocation,
        originalPreviousLocation: undefined
      } : location.state
      
      navigate(
        {
          pathname: navigateTo,
          search: `?${createSearchParams(location.state?.params || {})}`,
        },
        { state: stateToPass }
      )
      return
    }

    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    navigate('/myavailability')
  }, [isExternalNavigation, navigate, isManager, location.state, isValidLocation])

  return { handleNavigation }
}

export default useBackButton
